/**
 * Model categories slice for the application store.
 *
 * Holds the data-driven model classification list (served by
 * GET /api/model-categories) so grouped pickers can build their labels and
 * ordering from live data instead of a hardcoded tier taxonomy.
 */

import { modelCategories } from '@/services/api';
import type { ModelCategory } from '@/services/api';
import { logger } from '@/utils/logger';

import type { StateCreator } from 'zustand';

/**
 * Model categories state and actions.
 */
export interface ModelCategoriesSlice {
  // State
  categories: ModelCategory[];
  categoriesLoaded: boolean;
  categoriesError: boolean;

  // Actions
  setCategories: (categories: ModelCategory[]) => void;
  setCategoriesLoaded: (loaded: boolean) => void;
  setCategoriesError: (error: boolean) => void;
  loadCategories: () => Promise<void>;
}

/**
 * Creates the model categories slice.
 *
 * `loadCategories` fetches once and flips `categoriesLoaded` regardless of
 * outcome so consumers know the request settled; a failure sets
 * `categoriesError` (kept separate from "loaded but empty") so the grouping
 * layer can degrade gracefully rather than collapse every model into "Other".
 */
export const createModelCategoriesSlice: StateCreator<
  ModelCategoriesSlice,
  [],
  [],
  ModelCategoriesSlice
> = (set, get) => ({
  // Initial state
  categories: [],
  categoriesLoaded: false,
  categoriesError: false,

  // Actions
  setCategories: categories => set({ categories }),

  setCategoriesLoaded: loaded => set({ categoriesLoaded: loaded }),

  setCategoriesError: error => set({ categoriesError: error }),

  loadCategories: async () => {
    const state = get();

    // Don't reload if already settled.
    if (state.categoriesLoaded) {
      return;
    }

    try {
      set({ categoriesError: false });
      const categories = await modelCategories.getAll();
      set({ categories, categoriesLoaded: true });
    } catch (error) {
      logger.error('Failed to load model categories:', error);
      set({ categories: [], categoriesError: true, categoriesLoaded: true });
    }
  },
});
